import React from 'react';
import type { StoryModel } from './storyModel';
import { ThemeRegistry, THEME_OVERRIDE_KEYS, type ThemeOverrideKey } from '../../engine/theme';

/**
 * ThemePanel — pick a preset, adjust the few values a customer actually touches.
 *
 * Writes only `theme:` and the THEME_OVERRIDE_KEYS into the GLOBAL header through
 * StoryModel.setGlobals; the engine expands the preset on the next live reload, so the
 * 3D view IS the preview. Layout, camera and spacing are deliberately not here: they
 * belong to the content, not to the look.
 */

const RED = '#C1121C';
const CREAM = '#FBF7F4';
const sans = "system-ui, -apple-system, 'Segoe UI', sans-serif";
const serif = "'Iowan Old Style', Palatino, Georgia, serif";

/** Must stay a subset of AppSettingsData.labelFontFamily options. */
const FONTS = ['Georgia', 'Arial', 'Helvetica', 'Times New Roman', 'Courier New'];

const glass: React.CSSProperties = {
    background: 'rgba(20,10,12,0.82)',
    backdropFilter: 'blur(16px)',
    WebkitBackdropFilter: 'blur(16px)',
    border: '1px solid rgba(251,247,244,0.16)',
    color: CREAM,
    fontFamily: sans,
};

const inputStyle: React.CSSProperties = {
    width: '100%', boxSizing: 'border-box', background: 'rgba(251,247,244,0.08)',
    border: '1px solid rgba(251,247,244,0.2)', borderRadius: 9, padding: '8px 10px',
    color: CREAM, fontSize: 13.5, fontFamily: sans,
};

/** `#rrggbb` or `#rrggbbaa` → { rgb, alpha 0..1 }. Anything else → null. */
function splitHex(value: string | undefined): { rgb: string; alpha: number } | null {
    if (!value) return null;
    const m = value.trim().match(/^#([0-9a-fA-F]{6})([0-9a-fA-F]{2})?$/);
    if (!m) return null;
    return { rgb: `#${m[1].toLowerCase()}`, alpha: m[2] ? parseInt(m[2], 16) / 255 : 1 };
}

function joinHex(rgb: string, alpha: number): string {
    const a = Math.round(Math.max(0, Math.min(1, alpha)) * 255);
    return a >= 255 ? rgb : `${rgb}${a.toString(16).padStart(2, '0')}`;
}

export interface ThemePanelProps {
    model: StoryModel;
    narrow: boolean;
    onClose: () => void;
}

export const ThemePanel: React.FC<ThemePanelProps> = ({ model, narrow, onClose }) => {
    const registry = ThemeRegistry.Instance;
    const presets = registry.all();
    const globals = model.globals;
    const activeName = globals.theme ? ThemeRegistry.normalize(globals.theme) : '';
    const active = activeName ? registry.get(activeName) : null;
    const unknownTheme = Boolean(activeName) && !active;

    const effective = (key: ThemeOverrideKey): string => {
        const own = globals[key];
        if (own !== undefined && own !== '') return own;
        const fromPreset = active?.values[key];
        return fromPreset === undefined ? '' : String(fromPreset);
    };
    const isOverridden = (key: ThemeOverrideKey) => globals[key] !== undefined && globals[key] !== '';
    const hasOverrides = THEME_OVERRIDE_KEYS.some(isOverridden);

    const choosePreset = (name: string) => {
        // A fresh preset shows itself pure: overrides from the previous look are dropped.
        const patch: Record<string, string | undefined> = { theme: name };
        for (const key of THEME_OVERRIDE_KEYS) patch[key] = undefined;
        model.setGlobals(patch);
    };

    const setKey = (key: ThemeOverrideKey, value: string | undefined) => model.setGlobals({ [key]: value });

    const resetOverrides = () => {
        const patch: Record<string, string | undefined> = {};
        for (const key of THEME_OVERRIDE_KEYS) patch[key] = undefined;
        model.setGlobals(patch);
    };

    const field = (label: string, node: React.ReactNode) => (
        <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 11, letterSpacing: 2, textTransform: 'uppercase', opacity: 0.6, marginBottom: 6, fontWeight: 700 }}>{label}</div>
            {node}
        </div>
    );

    const colorField = (label: string, key: ThemeOverrideKey, withAlpha: boolean) => {
        const parsed = splitHex(effective(key)) ?? { rgb: '#000000', alpha: 1 };
        return field(label, (
            <div style={{ display: 'grid', gridTemplateColumns: withAlpha ? '44px 1fr' : '44px', gap: 8, alignItems: 'center' }}>
                <input
                    type="color"
                    data-testid={`theme-${key}`}
                    value={parsed.rgb}
                    onChange={e => setKey(key, joinHex(e.target.value, parsed.alpha))}
                    style={{ width: 44, height: 32, padding: 0, border: '1px solid rgba(251,247,244,0.3)', borderRadius: 7, background: 'transparent', cursor: 'pointer' }}
                />
                {withAlpha && (
                    <input
                        type="range" min={0} max={100} step={5}
                        value={Math.round(parsed.alpha * 100)}
                        title="Deckkraft"
                        onChange={e => setKey(key, joinHex(parsed.rgb, parseInt(e.target.value, 10) / 100))}
                        style={{ width: '100%', accentColor: RED }}
                    />
                )}
            </div>
        ));
    };

    return (
        <div
            data-testid="theme-panel"
            style={{
                ...glass, position: 'absolute', zIndex: 1240, borderRadius: 16, padding: 18,
                ...(narrow
                    ? { left: 12, right: 12, bottom: 128, maxHeight: '52vh', overflowY: 'auto' }
                    : { left: 16, top: 70, width: 306, maxHeight: 'calc(100vh - 210px)', overflowY: 'auto' }),
            }}
        >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                <div style={{ fontFamily: serif, fontSize: 19 }}>Thema</div>
                <div onClick={onClose} style={{ cursor: 'pointer', opacity: 0.6, fontSize: 18, lineHeight: 1 }}>×</div>
            </div>

            {unknownTheme && (
                <div style={{ color: '#ff9ca3', fontSize: 12.5, marginBottom: 12 }}>
                    Unbekanntes Thema „{globals.theme}“ im Markdown. Wähle eines der Themen unten.
                </div>
            )}

            {field('Vorlage', (
                <div style={{ display: 'grid', gap: 7 }}>
                    {presets.map(p => {
                        const bg1 = String(p.values.backgroundGradientColor1 ?? '#000');
                        const bg2 = String(p.values.backgroundGradientColor2 ?? bg1);
                        const text = String(p.values.labelColor ?? '#fff');
                        const selected = p.name === activeName;
                        return (
                            <button
                                key={p.name}
                                data-testid={`theme-preset-${p.name}`}
                                onClick={() => choosePreset(p.name)}
                                title={p.description}
                                style={{
                                    display: 'grid', gridTemplateColumns: '54px 1fr', gap: 10, alignItems: 'center', textAlign: 'left',
                                    background: selected ? 'rgba(193,18,28,0.35)' : 'rgba(251,247,244,0.06)',
                                    border: `1px solid ${selected ? RED : 'rgba(251,247,244,0.18)'}`,
                                    color: CREAM, borderRadius: 10, padding: 7, cursor: 'pointer', fontFamily: sans,
                                }}
                            >
                                <span style={{
                                    display: 'grid', placeItems: 'center', height: 34, borderRadius: 7,
                                    background: `linear-gradient(135deg, ${bg1}, ${bg2})`,
                                    color: text, fontFamily: String(p.values.labelFontFamily ?? 'Georgia'), fontSize: 15,
                                }}>Aa</span>
                                <span>
                                    <span style={{ display: 'block', fontSize: 13.5, fontWeight: 600 }}>{p.label}</span>
                                    <span style={{ display: 'block', fontSize: 11.5, opacity: 0.65, lineHeight: 1.35 }}>{p.description}</span>
                                </span>
                            </button>
                        );
                    })}
                </div>
            ))}

            <div style={{ fontSize: 11, letterSpacing: 2, textTransform: 'uppercase', opacity: 0.6, margin: '6px 0 10px', fontWeight: 700 }}>
                Anpassen
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div>{colorField('Hintergrund oben', 'backgroundGradientColor1', false)}</div>
                <div>{colorField('Hintergrund unten', 'backgroundGradientColor2', false)}</div>
            </div>
            {field('Verlaufswinkel', (
                <input
                    type="range" min={0} max={360} step={5}
                    value={parseInt(effective('backgroundGradientAngle') || '180', 10)}
                    onChange={e => setKey('backgroundGradientAngle', e.target.value)}
                    style={{ width: '100%', accentColor: RED }}
                />
            ))}
            {colorField('Schriftfarbe', 'labelColor', false)}
            {colorField('Schrifthintergrund und Deckkraft', 'labelBackgroundColor', true)}
            {field('Schrift', (
                <select
                    data-testid="theme-labelFontFamily"
                    value={effective('labelFontFamily') || 'Georgia'}
                    onChange={e => setKey('labelFontFamily', e.target.value)}
                    style={{ ...inputStyle, cursor: 'pointer' }}
                >
                    {FONTS.map(f => <option key={f} value={f} style={{ color: '#000' }}>{f}</option>)}
                </select>
            ))}

            <button
                onClick={resetOverrides}
                disabled={!hasOverrides}
                style={{
                    background: 'transparent', border: '1px solid rgba(251,247,244,0.25)', color: CREAM,
                    borderRadius: 9, padding: '9px 14px', fontSize: 13, width: '100%',
                    cursor: hasOverrides ? 'pointer' : 'default', opacity: hasOverrides ? 0.85 : 0.35, fontFamily: sans,
                }}
            >
                Anpassungen zurücksetzen
            </button>
        </div>
    );
};

export default ThemePanel;
